import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdvertisementEventServices } from '../../apis/advertisementEventService';
import { toast } from 'react-toastify';
import { Save, X, ArrowLeft, Image } from 'lucide-react';

const EditAdvertisementEventPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAdvertisementEventById, updateAdvertisementEvent, loading } = useAdvertisementEventServices();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventType: 'conference',
    startDate: '',
    endDate: '',
    location: '',
    isOnline: false,
    isFeatured: false,
    isActive: true,
    actionButtonText: '',
    actionButtonUrl: '',
    actionButtonColor: '#3B82F6',
    sponsorDayDate: '',
    deadlineDate: '',
    displayOrder: 0,
    backgroundColor: '',
    icon: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEventData();
  }, [id]);

  const fetchEventData = async () => {
    try {
      const response = await getAdvertisementEventById(id);
      if (response.success) {
        const event = response.data;
        setFormData({
          title: event.title || '',
          description: event.description || '',
          eventType: event.eventType || 'conference',
          startDate: event.startDate ? new Date(event.startDate).toISOString().slice(0, 16) : '',
          endDate: event.endDate ? new Date(event.endDate).toISOString().slice(0, 16) : '',
          location: event.location || '',
          isOnline: event.isOnline || false,
          isFeatured: event.isFeatured || false,
          isActive: event.isActive !== undefined ? event.isActive : true,
          actionButtonText: event.actionButtonText || '',
          actionButtonUrl: event.actionButtonUrl || '',
          actionButtonColor: event.actionButtonColor || '#3B82F6',
          sponsorDayDate: event.sponsorDayDate ? new Date(event.sponsorDayDate).toISOString().slice(0, 10) : '',
          deadlineDate: event.deadlineDate ? new Date(event.deadlineDate).toISOString().slice(0, 16) : '',
          displayOrder: event.displayOrder || 0,
          backgroundColor: event.backgroundColor || '',
          icon: event.icon || ''
        });
        if (event.imageUrl) {
          setImagePreview(event.imageUrl);
        }
      } else {
        toast.error('Failed to fetch event data');
        navigate('/super-admin/advertisement-event-list');
      }
    } catch (err) {
      toast.error('An error occurred while fetching event data');
      navigate('/super-admin/advertisement-event-list');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image file size should be less than 2MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const eventData = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        sponsorDayDate: formData.sponsorDayDate ? new Date(formData.sponsorDayDate).toISOString() : null,
        deadlineDate: formData.deadlineDate ? new Date(formData.deadlineDate).toISOString() : null,
        displayOrder: parseInt(formData.displayOrder) || 0,
        // Ensure boolean fields are actual booleans, not strings
        isOnline: formData.isOnline === true || formData.isOnline === 'true',
        isFeatured: formData.isFeatured === true || formData.isFeatured === 'true',
        isActive: formData.isActive === true || formData.isActive === 'true'
      };

      const response = await updateAdvertisementEvent(id, eventData, imageFile);
      if (response.success) {
        toast.success('Event updated successfully');
        navigate('/super-admin/advertisement-event-list');
      } else {
        toast.error(response.message || 'Failed to update event');
      }
    } catch (err) {
      toast.error('An error occurred while updating the event');
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/super-admin/advertisement-event-list')}
          className="mr-4 p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Edit Advertisement Event</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`w-full p-2 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-md`}
            />
            {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`w-full p-2 border ${errors.description ? 'border-red-500' : 'border-gray-300'} rounded-md`}
            />
            {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select name="eventType" value={formData.eventType} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md">
              <option value="conference">Conference</option>
              <option value="expo">Expo</option>
              <option value="meetup">Meetup</option>
              <option value="summit">Summit</option>
              <option value="workshop">Workshop</option>
              <option value="seminar">Seminar</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              className={`w-full p-2 border ${errors.startDate ? 'border-red-500' : 'border-gray-300'} rounded-md`}
            />
            {errors.startDate && <p className="mt-1 text-sm text-red-500">{errors.startDate}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="datetime-local"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isOnline"
              checked={formData.isOnline}
              onChange={handleInputChange}
              className="h-4 w-4 text-green-600"
            />
            <label className="ml-2 text-sm text-gray-700">Online Event</label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action Button Text</label>
            <input
              type="text"
              name="actionButtonText"
              value={formData.actionButtonText}
              onChange={handleInputChange}
              placeholder="e.g., Get Tickets, Register"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action Button URL</label>
            <input
              type="url"
              name="actionButtonUrl"
              value={formData.actionButtonUrl}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Image</label>
            {imagePreview ? (
              <div className="flex items-start space-x-4">
                <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                <button type="button" onClick={removeImage} className="p-2 bg-red-500 text-white rounded">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer">
                <Image size={16} className="mr-2" />
                Upload Image
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
            )}
          </div>

          <div className="flex space-x-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="h-4 w-4 text-green-600"
              />
              <label className="ml-2 text-sm text-gray-700">Active</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleInputChange}
                className="h-4 w-4 text-green-600"
              />
              <label className="ml-2 text-sm text-gray-700">Featured</label>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/super-admin/advertisement-event-list')}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
          >
            <Save size={18} className="mr-2" />
            {loading ? 'Updating...' : 'Update Event'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditAdvertisementEventPage;

